import React, { useState, useCallback, useEffect, useImperativeHandle, useMemo, useRef, forwardRef } from 'react';
import {
	View,
	Pressable,
	ScrollView,
	StyleSheet,
	TextInputProps,
	TouchableOpacity,
	Platform,
	type ViewInstance,
} from 'react-native';

import { SearchModal } from './search-modal';

import {
	Text,
	useTheme,
	Portal,
	TextInput,
	TextInputIcon,
	Icon,
	Surface,
} from '@/components/ui';
// SUBPATH, not the `@/hooks` barrel. The barrel re-exports `use-pull-to-refresh`, which
// imports `expo-haptics` — and ES module evaluation loads every re-exported file's top-level
// imports even when one named export is used. Anything importing this typeahead therefore
// pulled `expo-modules-core` into its graph, which a jsdom spec cannot parse. Same trap as
// `@/components/ui`; see the ui-barrel-imports-break-tests skill.
import { useIsLargeScreen } from '@/hooks/use-is-large-screen';
import { spacing, typeaheadSearchStyles as styles } from '@/styles';

export type TypeaheadOption = {
	id: string;
	label: string;
	description?: string;
	lat?: number;
	lng?: number;
	type?: 'address' | 'shortcut' | 'account';
	shortcutCode?: string;
	associationId?: string;
	accountId?: string;
	/**
	 * Text to put in the FIELD when this option is chosen, when that differs from the
	 * text on the row. Defaults to `label`.
	 *
	 * Needed wherever a row is labelled by something you must never save: an account
	 * matched by owner name reads "John & Jane Smith" but has to commit the lot (#438).
	 * Without this, tabbing away writes a person's name into a location field.
	 */
	fillValue?: string;
};

/** What this option puts in the field — the row label unless it says otherwise. */
const committedTextOf = (option: TypeaheadOption): string => option.fillValue ?? option.label;

export type TypeaheadSearchProps = {
	placeholder?: string;
	onSearch: (text: string) => void;
	onSelect: (option: TypeaheadOption | null) => void;
	options: TypeaheadOption[];
	loading?: boolean;
	error?: string;
	debounceMs?: number;
	minSearchLength?: number;
	maxResults?: number;
	renderOption?: (option: TypeaheadOption) => React.ReactNode;
	selectedOption?: TypeaheadOption | null;
	label?: string;
	style?: TextInputProps['style'];
	testID?: string;
	autoFocus?: boolean;
	allowCreate?: boolean;
	/**
	 * Commit free-typed text on blur (tab/click-away) as if the user picked the
	 * "Use: <text>" row. Opt-in: enable ONLY where a custom value is a harmless
	 * free-text fill (e.g. the work-order address). Leave off for wrappers that
	 * CREATE an entity from `custom:` selections — blur is too implicit an
	 * action to trigger a write. Enter still commits typed text everywhere.
	 */
	commitOnBlur?: boolean;
	/**
	 * Let Tab complete to the ONLY option in the list even when the typed text isn't a
	 * prefix of it (#293). Right for pickers, where every value must come from the list.
	 *
	 * Turn OFF for free-text fields: with it on, typing a real location like "Community"
	 * and tabbing away silently replaces it with whichever lone suggestion happened to be
	 * showing. Genuine prefix completion keeps working either way.
	 */
	tabCompleteLoneOption?: boolean;
	/**
	 * Seed the search text (and the desktop dropdown, opened automatically) on
	 * mount without marking anything as selected — e.g. pre-fill a name parsed
	 * from an email so staff pick from the matches instead of retyping it.
	 * Uncontrolled: read once at mount, not reactive to later prop changes.
	 */
	initialQuery?: string;
};

/**
 * Imperative escape hatch for callers that must act on typed text BEFORE the deferred
 * blur commit lands (see `commitOnBlur` and the 200ms timeout in `onBlur`). A form
 * validating on Save otherwise reads state the user has visibly already filled in.
 */
export interface TypeaheadSearchHandle {
	/** Commit any uncommitted typed text right now. Safe to call when there is none. */
	flushPendingText: () => void;
}

export const TypeaheadSearch = forwardRef<TypeaheadSearchHandle, TypeaheadSearchProps>(({
	placeholder = 'Search...',
	onSearch,
	onSelect,
	options,
	loading = false,
	error,
	maxResults = 10,
	selectedOption,
	label,
	style,
	testID,
	autoFocus = false,
	allowCreate = true,
	commitOnBlur = false,
	tabCompleteLoneOption = true,
	initialQuery,
}, ref) => {
	const [isMenuVisible, setIsMenuVisible] = useState(false);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [searchQuery, setSearchQuery] = useState(initialQuery ?? '');
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const inputRef = useRef<ViewInstance>(null);
	// Latest-value refs for the deferred blur commit: the 200ms blur timeout
	// (which exists so option clicks land before the menu closes) would
	// otherwise read stale closure state and re-commit typed text over a
	// selection the user just clicked.
	const searchQueryRef = useRef(searchQuery);
	searchQueryRef.current = searchQuery;
	const selectedOptionRef = useRef(selectedOption);
	selectedOptionRef.current = selectedOption;
	const filteredOptionsRef = useRef<TypeaheadOption[]>([]);
	const [anchorRect, setAnchorRect] = useState<{ top: number; left: number; width: number } | null>(null);
	const isLargeScreen = useIsLargeScreen();
	const theme = useTheme();

	const filteredOptions = options.slice(0, maxResults);
	filteredOptionsRef.current = filteredOptions;

	// Check if current search query is an exact match with any option. Compared against
	// the COMMITTED text: a row labelled by owner name must not suppress "Use: <text>"
	// for someone typing that same name as a literal location.
	const hasExactMatch = filteredOptions.some(option =>
		committedTextOf(option).toLowerCase() === searchQuery.toLowerCase(),
	);

	// Create "Use: <text>" option if user has typed something and it's not an exact match
	let useCustomOption: TypeaheadOption | null = null;

	if (allowCreate && searchQuery.trim() && !hasExactMatch) {
		useCustomOption = {
			id: `custom:${searchQuery}`,
			label: searchQuery,
			description: `Use: "${searchQuery}"`,
		};
	}

	// Combine custom option with filtered options
	const allOptions = useMemo(
		() => useCustomOption ? [useCustomOption, ...filteredOptions] : filteredOptions,
		[useCustomOption, filteredOptions],
	);

	// Tab-completion target (#293): when the typed text resolves to exactly ONE
	// real option, Tab auto-completes it. Prefer a prefix match ("Benn" →
	// "Bennington Cove"); fall back to the lone option when only one exists.
	// Render-only (no state) so it never perturbs the value↔text / blur races.
	const solePrefixCandidate = useMemo<TypeaheadOption | null>(() => {
		const q = searchQuery.trim().toLowerCase();
		if (!q) return null;
		if (selectedOption && committedTextOf(selectedOption).toLowerCase() === q) return null;
		// Match on either face of the option: staff tab-complete from what they SEE
		// (an owner name) as readily as from what lands in the field (the lot).
		const prefixMatches = filteredOptions.filter(o =>
			o.label.toLowerCase().startsWith(q) || committedTextOf(o).toLowerCase().startsWith(q));
		if (prefixMatches.length === 1) return prefixMatches[0];
		if (tabCompleteLoneOption && filteredOptions.length === 1) return filteredOptions[0];
		return null;
	}, [searchQuery, filteredOptions, selectedOption, tabCompleteLoneOption]);

	// When the user hasn't arrow-navigated (highlightedIndex < 0) but a sole
	// candidate exists, highlight it in the dropdown so Tab's target is visible.
	const tabHintIndex = useMemo(
		() => (solePrefixCandidate ? allOptions.findIndex(o => o.id === solePrefixCandidate.id) : -1),
		[solePrefixCandidate, allOptions],
	);
	const effectiveHighlightIndex = highlightedIndex >= 0 ? highlightedIndex : tabHintIndex;

	const onChangeText = useCallback((text: string) => {
		// If there's a selected option and the text is different (user is typing/backspacing),
		// clear the selection
		if (selectedOption && text !== committedTextOf(selectedOption)) {
			onSelect(null);
			setSearchQuery(text);
			setHighlightedIndex(-1);
			onSearch(text);
			return;
		}

		setSearchQuery(text);
		setHighlightedIndex(-1); // Reset highlighted index when text changes
		onSearch(text);
	}, [onSearch, selectedOption, onSelect]);

	const handleSelect = useCallback((option: TypeaheadOption) => {
		onSelect(option);
		// Sync the query to the chosen option's COMMITTED text so the deferred blur
		// commit can tell "typed text the user abandoned" from "text resolved by a click".
		setSearchQuery(committedTextOf(option));
		setIsMenuVisible(false);
		setIsModalVisible(false);
		setHighlightedIndex(-1);
	}, [onSelect]);

	// Commit free-typed text as the synthetic `custom:<text>` option — the same
	// path as clicking the "Use: <text>" row — so tabbing/clicking away doesn't
	// silently discard the value.
	const commitTypedText = useCallback(() => {
		if (!allowCreate) return;
		const text = searchQueryRef.current.trim();
		const selected = selectedOptionRef.current;
		if (!text || (selected && committedTextOf(selected) === text)) return;
		// Prefer the real option on a case-insensitive exact match — committing
		// `custom:<text>` here would route wrappers into their create path and
		// duplicate an existing entry (e.g. "treasurer" vs "Treasurer"). Matched on
		// committed text, so typing an owner name still commits as free text.
		const exact = filteredOptionsRef.current.find(
			option => committedTextOf(option).toLowerCase() === text.toLowerCase(),
		);
		if (exact) {
			handleSelect(exact);
			return;
		}
		handleSelect({ id: `custom:${text}`, label: text, description: `Use: "${text}"` });
	}, [allowCreate, handleSelect]);

	// Only meaningful where blur already commits: elsewhere typed text is not a value
	// until the user picks something, and forcing it would invent a selection.
	useImperativeHandle(ref, () => ({
		flushPendingText: () => {
			if (commitOnBlur) commitTypedText();
		},
	}), [commitOnBlur, commitTypedText]);

	// Desktop layout only on web - iPad/iOS uses modal for better touch handling
	const useDesktopLayout = isLargeScreen && Platform.OS === 'web';

	// A seeded query is pointless if the matches stay hidden — open the desktop
	// dropdown immediately so staff see the pre-filtered list without an extra
	// click into the field. Mount-only (mirrors initialQuery's uncontrolled seed).
	useEffect(() => {
		if (initialQuery && useDesktopLayout) setIsMenuVisible(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only
	}, []);

	// Measure the input position so the dropdown can be portaled out of any
	// parent stacking context and still appear directly below the field.
	// Without this, sibling cards / DataTable rows / modals with their own
	// z-index can paint over the panel even when the parent View has a high
	// zIndex set on it.
	const measureAnchor = useCallback(() => {
		const node = inputRef.current as unknown as { getBoundingClientRect?: () => DOMRect; measureInWindow?: (cb: (x: number, y: number, w: number, h: number) => void) => void } | null;
		if (!node) return;
		if (Platform.OS === 'web' && typeof node.getBoundingClientRect === 'function') {
			const r = node.getBoundingClientRect();
			setAnchorRect({ top: r.bottom, left: r.left, width: r.width });
		} else if (typeof node.measureInWindow === 'function') {
			node.measureInWindow((x, y, w, h) => setAnchorRect({ top: y + h, left: x, width: w }));
		}
	}, []);

	useEffect(() => {
		if (!isMenuVisible || !useDesktopLayout) return;
		measureAnchor();
		if (Platform.OS !== 'web') return;
		// Re-measure on scroll (capture phase to catch nested scroll containers)
		// and resize so the dropdown stays glued to the input as the page moves.
		const onMove = () => measureAnchor();
		window.addEventListener('scroll', onMove, true);
		window.addEventListener('resize', onMove);
		return () => {
			window.removeEventListener('scroll', onMove, true);
			window.removeEventListener('resize', onMove);
		};
	}, [isMenuVisible, useDesktopLayout, measureAnchor]);

	const openSearch = () => {
		if (useDesktopLayout) {
			setIsMenuVisible(true);
		} else {
			setIsModalVisible(true);
		}
	};

	const closeSearch = () => {
		setIsMenuVisible(false);
		setIsModalVisible(false);
		setHighlightedIndex(-1);
	};

	const handleKeyPress = useCallback((event: any) => {
		const key = event.nativeEvent.key;

		// Handle backspace/delete when a selection exists - clear it immediately
		if ((key === 'Backspace' || key === 'Delete') && selectedOption) {
			event.preventDefault();
			setHighlightedIndex(-1);
			// Ensure menu stays visible and ready for input
			if (isLargeScreen) {
				setIsMenuVisible(true);
			}
			// Trigger onChangeText to clear selection and sync input state
			// onChangeText will handle clearing since text ('') !== selectedOption.label
			onChangeText('');
			return;
		}

		if (!isMenuVisible || allOptions.length === 0) return;

		switch (key) {
			case 'ArrowDown':
				event.preventDefault();
				setHighlightedIndex((prevIndex) =>
					prevIndex < allOptions.length - 1 ? prevIndex + 1 : 0,
				);
				break;
			case 'ArrowUp':
				event.preventDefault();
				setHighlightedIndex((prevIndex) =>
					prevIndex > 0 ? prevIndex - 1 : allOptions.length - 1,
				);
				break;
			case 'Enter':
				event.preventDefault();
				if (highlightedIndex >= 0 && highlightedIndex < allOptions.length) {
					handleSelect(allOptions[highlightedIndex]);
				} else {
					commitTypedText();
				}
				break;
			case 'Tab':
				// Auto-complete only when the typed text resolves to a single
				// candidate; otherwise let Tab move focus normally (never trap it).
				if (solePrefixCandidate) {
					event.preventDefault();
					handleSelect(solePrefixCandidate);
				}
				break;
			case 'Escape':
				event.preventDefault();
				closeSearch();
				break;
		}
	}, [isMenuVisible, allOptions, highlightedIndex, handleSelect, selectedOption, onChangeText, isLargeScreen, commitTypedText, solePrefixCandidate]);

	const renderOptionItem = ({ item }: { item: TypeaheadOption }) => {
		const isCustomOption = item.id.startsWith('custom:');
		return (
			<TouchableOpacity
				activeOpacity={0.7}
				delayPressIn={0}
				onPress={() => handleSelect(item)}
				style={styles.modalOptionItem}
			>
				<Text
					variant="bodyLarge"
					style={{ fontWeight: isCustomOption ? 'bold' : 'normal' }}
				>
					{isCustomOption ? item.description : item.label}
				</Text>
				{item.description && !isCustomOption && (
					<Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
						{item.description}
					</Text>
				)}
			</TouchableOpacity>
		);
	};

	if (useDesktopLayout) {
		return (
			<View
				style={{
					position: 'relative',
					zIndex: 10,
					display: 'flex',
					flex: 1,
					flexDirection: 'column',
					gap: spacing.m,
					marginBottom: spacing.m,
					elevation: 10,
				}}
			>
				<View ref={inputRef}>
					<TextInput
						mode="outlined"
						label={label}
						style={style}
						placeholder={placeholder}
						value={selectedOption ? committedTextOf(selectedOption) : searchQuery}
						onChangeText={onChangeText}
						onKeyPress={handleKeyPress}
						onFocus={() => {
							setIsMenuVisible(true);
							setHighlightedIndex(-1);
						}}
						onBlur={() => {
							// Deferred so a click on a dropdown option wins the race;
							// by the time this runs the refs reflect any selection made.
							setTimeout(() => {
								setIsMenuVisible(false);
								setHighlightedIndex(-1);
								if (commitOnBlur) commitTypedText();
							}, 200);
						}}
						right={
							selectedOption ? (
								<TextInputIcon
									icon="close"
									onPress={() => {
										setSearchQuery('');
										onSelect(null);
										onSearch('');
										setHighlightedIndex(-1);
									}}
								/>
							) : null
						}
						testID={testID}
						autoFocus={autoFocus}
					/>
				</View>

				{isMenuVisible && anchorRect && (
					<Portal>
						<View
							testID={testID ? `${testID}-dropdown` : 'typeahead-dropdown'}
							style={{
								// `fixed` keeps the panel anchored to the viewport so it
								// escapes every parent's stacking context. We re-measure
								// on scroll/resize so it stays glued to the input.
								position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
								top: anchorRect.top + 4,
								left: anchorRect.left,
								width: anchorRect.width,
								zIndex: 99999,
								maxHeight: 300,
							}}
						>
							<Surface
								style={{
									backgroundColor: 'white',
									borderColor: '#ccc',
									borderWidth: 1,
									borderRadius: 4,
									elevation: 24,
									shadowColor: '#000',
									shadowOffset: { width: 0, height: 4 },
									shadowOpacity: 0.2,
									shadowRadius: 8,
									maxHeight: 300,
								}}
								elevation={5}
							>
								<ScrollView style={{ maxHeight: 300 }} nestedScrollEnabled keyboardShouldPersistTaps="always">
									{allOptions.length > 0 ? (
										allOptions.map((option, index) => {
											const isCustomOption = option.id.startsWith('custom:');
											return (
												<Pressable
													style={{
														padding: 12,
														borderBottomWidth: StyleSheet.hairlineWidth,
														borderBottomColor: '#eee',
														backgroundColor: index === effectiveHighlightIndex ? theme.colors.surfaceVariant : 'transparent',
													}}
													key={option.id}
													onPress={() => handleSelect(option)}
												>
													<Text style={{
														color: index === effectiveHighlightIndex ? theme.colors.onSurfaceVariant : theme.colors.onSurface,
														fontWeight: isCustomOption ? 'bold' : 'normal',
													}}>
														{isCustomOption ? option.description : option.label}
													</Text>
													{option.description && !isCustomOption && (
														<Text style={{
															fontSize: 12,
															color: theme.colors.onSurfaceVariant,
														}}>
															{option.description}
														</Text>
													)}
												</Pressable>
											);
										})
									) : (
										<Text style={{ padding: 12, textAlign: 'center' }}>
											No results found
										</Text>
									)}
								</ScrollView>
							</Surface>
						</View>
					</Portal>
				)}
			</View>
		);
	} else if (isModalVisible) {
		return (
			<Portal>
				<SearchModal
					isModalVisible={isModalVisible}
					closeSearch={closeSearch}
					placeholder={placeholder}
					onChangeText={onChangeText}
					searchQuery={searchQuery}
					filteredOptions={allOptions}
					renderOptionItem={renderOptionItem}
				/>
			</Portal>
		);
	} else {
		return (
			<View style={styles.container}>
				{label && <Text style={styles.label}>{label}</Text>}
				{/*
					Boxed like the outlined TextInputs around it (#409). Bare, the icon and placeholder
					floated on the page and did not read as a field on a phone.
				*/}
				<View
					testID={testID ? `${testID}-field` : undefined}
					style={[
						styles.smallScreenField,
						{ borderColor: theme.colors.outline, backgroundColor: theme.colors.surface },
					]}
				>
					<TouchableOpacity
						activeOpacity={0.7}
						onPress={openSearch}
						style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}
					>
						<Icon source="search" size={24} />
						<Text style={{
							marginLeft: 8,
							color: selectedOption ? theme.colors.onSurface : theme.colors.onSurfaceVariant,
						}}>
							{selectedOption?.label || placeholder}
						</Text>
					</TouchableOpacity>
					<TouchableOpacity
						activeOpacity={0.7}
						onPress={() => onSelect(null)}
					>
						<Icon source="close" size={24} />
					</TouchableOpacity>
				</View>
			</View>
		);
	}
});

TypeaheadSearch.displayName = 'TypeaheadSearch';
